// Hypertrophy volume landmarks — weekly HARD-set targets per muscle, used by the History
// "weekly sets per muscle" card to turn a raw set count into actionable, *educational*
// coaching. These are the Renaissance Periodization (Israetel et al.) volume landmarks,
// widely cited as rough population averages:
//   MEV — Minimum Effective Volume:   fewest weekly hard sets that still build the muscle.
//   MAV — Maximum Adaptive Volume:    top of the range where most growth happens (the
//                                     productive sweet spot is MEV → MAV).
//   MRV — Maximum Recoverable Volume: the most you can do and still recover; past it,
//                                     fatigue outpaces gains and a deload is due.
// Numbers are per-week and vary by person/exercise/life stress — a guide, not a rule.
// Keyed by lowercase muscle name (the catalog's stable key; see server/seed-library.ts).
export const MUSCLE_LANDMARKS = {
  abdominals: { mev: 0, mav: 16, mrv: 25 },
  abductors: { mev: 0, mav: 12, mrv: 20 },
  adductors: { mev: 0, mav: 12, mrv: 20 },
  biceps: { mev: 8, mav: 16, mrv: 26 },
  calves: { mev: 8, mav: 14, mrv: 20 },
  chest: { mev: 8, mav: 16, mrv: 22 },
  forearms: { mev: 4, mav: 10, mrv: 16 },
  glutes: { mev: 4, mav: 12, mrv: 16 },
  hamstrings: { mev: 6, mav: 13, mrv: 20 },
  lats: { mev: 10, mav: 18, mrv: 25 },
  "lower back": { mev: 4, mav: 10, mrv: 16 },
  "middle back": { mev: 10, mav: 18, mrv: 25 },
  neck: { mev: 0, mav: 8, mrv: 16 },
  quadriceps: { mev: 8, mav: 15, mrv: 20 },
  shoulders: { mev: 8, mav: 18, mrv: 26 },
  traps: { mev: 0, mav: 14, mrv: 26 },
  triceps: { mev: 6, mav: 12, mrv: 18 },
};

// Fallback for any muscle not in the table (keeps the card robust to catalog additions).
const DEFAULT_LANDMARK = { mev: 6, mav: 14, mrv: 22 };

export function landmarksFor(muscleName) {
  return MUSCLE_LANDMARKS[(muscleName || "").toLowerCase()] || DEFAULT_LANDMARK;
}

// Map a weekly hard-set count to a training zone, with a short coaching nudge. Four zones
// from three landmarks: below MEV (under-stimulating) · MEV–MAV (productive) · MAV–MRV
// (high/diminishing) · above MRV (overreaching). `abbr` is the landmark a user has reached
// or fallen short of, so the UI can teach the term in context.
export function setZone(perWeek, lm) {
  // MEV-0 muscles (abs, traps, neck, ab/adductors) are maintained by compound work alone —
  // calling 1–2 direct sets a "growth zone" would overstate it. Neutral label until the
  // volume is a deliberate dose (past MAV the normal high/over zones apply).
  if (lm.mev === 0 && perWeek <= lm.mav)
    return {
      key: "indirect",
      label: "Covered indirectly",
      abbr: null,
      color: "var(--text-3)",
      nudge: "Maintained by indirect work from compound lifts — direct sets are optional unless it's a focus.",
    };
  if (perWeek < lm.mev)
    return {
      key: "below",
      label: "Below MEV",
      abbr: "MEV",
      color: "var(--warn)",
      nudge: `Below your minimum effective volume — add a few sets to drive growth (aim for ${lm.mev}+/wk).`,
    };
  if (perWeek <= lm.mav)
    return {
      key: "growth",
      label: "Growth zone",
      abbr: null,
      color: "var(--success)",
      nudge: "In the productive zone — keep it here and progress the load/reps.",
    };
  if (perWeek <= lm.mrv)
    return {
      key: "high",
      label: "High volume",
      abbr: "MAV",
      color: "#eab308",
      nudge: `Above your maximum adaptive volume (${lm.mav}/wk) — fine for a push, but watch recovery.`,
    };
  return {
    key: "over",
    label: "Above MRV",
    abbr: "MRV",
    color: "var(--danger)",
    nudge: `Past your maximum recoverable volume (${lm.mrv}/wk) — consider a lighter deload week.`,
  };
}

// Plain-language definitions for the in-card "(i)" explainer — the educational core.
export const VOLUME_GLOSSARY = [
  ["Hard set", "A working set taken close to failure — about RPE 7+ (0–3 reps left). Warm-ups and easy sets don't count toward growth."],
  ["MEV — Minimum Effective Volume", "The fewest hard sets per week that still build a muscle. Below it, you mostly maintain what you already have."],
  ["MAV — Maximum Adaptive Volume", "The upper end of the range where most growth happens. MEV → MAV is your productive sweet spot."],
  ["MRV — Maximum Recoverable Volume", "The most you can do and still recover from. Train above it for long and fatigue outpaces gains — time for a lighter “deload” week."],
];
